import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Pause, Play } from "lucide-react";
import EcosystemHome from "./EcosystemHome";
import { setVia, visitorId } from "../../lib/via";
import { CREAM, DIM, GOLD, SANS, SERIF } from "./sections";
import { StarterKit } from "./StarterKit";

const API = process.env.REACT_APP_BACKEND_URL;

// reclaim.app/with/<slug>: the ecosystem home, attributed to an influencer.
export function InfluencerEntry() {
  const { slug } = useParams();
  const [friend, setFriend] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    axios.get(`${API}/api/with/${slug}`).then((r) => {
      setVia(slug.toLowerCase());
      axios.post(`${API}/api/with/${slug}/visit`, { visitor_id: visitorId() }).catch(() => {});
      // Golf partners open the door to Soulumo first; the visit is credited to them.
      if (r.data.dest === "soulumo") { sessionStorage.setItem("demo_kind", "golf"); nav("/soulumo", { replace: true }); }
      else setFriend(r.data);
    }).catch(() => {});
  }, [slug, nav]);
  // Remount once the link is confirmed so the guide greets with the friend line.
  return <EcosystemHome key={friend?.slug || "plain"} friend={friend} />;
}

const PhotoUpload = ({ token, s, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setErr("");
    const fd = new FormData(); fd.append("photo", f);
    try { const r = await axios.post(`${API}/api/with-stats/${token}/photo`, fd); onDone(r.data.photo_url); }
    catch (x) { setErr(x?.response?.status === 413 ? "That photo is too large (max 8 MB)." : "Please choose a JPG, PNG or WebP photo."); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="mt-8 flex items-center gap-5" data-testid="influencer-photo-block">
      <div className="w-20 h-20 rounded-full overflow-hidden shrink-0 flex items-center justify-center" style={{ border: `1px solid ${GOLD}66`, background: "rgba(255,255,255,0.03)" }}>
        {s.photo_url ? <img src={`${API}${s.photo_url}`} alt={s.name} className="w-full h-full object-cover" data-testid="influencer-photo" /> : <span className="text-2xl" style={{ fontFamily: SERIF, color: GOLD }}>{s.name[0]}</span>}
      </div>
      <div>
        <label className="inline-flex cursor-pointer rounded-full px-4 py-2 text-xs uppercase tracking-[0.18em] transition-transform active:scale-95" style={{ border: `1px solid ${GOLD}`, color: GOLD, fontFamily: SANS }} data-testid="influencer-photo-upload">
          {busy ? "Uploading…" : s.photo_url ? "Change photo" : "Add your photo"}
          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick} disabled={busy} data-testid="influencer-photo-input" />
        </label>
        <p className="mt-2 text-xs" style={{ color: DIM, fontFamily: SANS }}>Shown beside Adelaida's welcome for people who arrive through your link.</p>
        {err && <p className="mt-1 text-xs" style={{ color: "#e9a39c" }} data-testid="influencer-photo-error">{err}</p>}
      </div>
    </div>
  );
};

const Stat = ({ label, value, id }) => (
  <div className="rounded-2xl p-5" style={{ border: "1px solid rgba(215,187,122,0.2)", background: "rgba(255,255,255,0.02)" }}>
    <p className="text-5xl font-light" style={{ fontFamily: SERIF, color: CREAM }} data-testid={`influencer-stat-${id}`}>{value}</p>
    <p className="mt-1 text-[0.6rem] uppercase tracking-[0.26em]" style={{ color: GOLD, fontFamily: SANS }}>{label}</p>
  </div>
);

const ThankYou = ({ t }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  return (
    <div className="mt-10 rounded-3xl p-6 flex items-start gap-5" style={{ border: `1px solid ${GOLD}55`, background: "radial-gradient(circle at 0% 0%, rgba(215,187,122,0.12), transparent 60%)" }} data-testid="influencer-thank-you">
      <button type="button" onClick={() => (playing ? ref.current.pause() : ref.current.play().catch(() => {}))} aria-label="Play Adelaida's message" data-testid="influencer-thank-you-play"
        className="relative w-16 h-16 rounded-full overflow-hidden shrink-0 transition-transform active:scale-95" style={{ boxShadow: `0 0 0 1px ${GOLD}, 0 0 30px -6px ${GOLD}` }}>
        <img src="/coaches/adelaida_calm.webp" alt="Adelaida" className="w-full h-full object-cover object-top" />
        <span className="absolute inset-0 flex items-center justify-center bg-black/35">{playing ? <Pause size={18} color={CREAM} /> : <Play size={18} color={CREAM} />}</span>
      </button>
      <div className="min-w-0">
        <p className="text-[0.6rem] uppercase tracking-[0.28em]" style={{ color: GOLD, fontFamily: SANS }}>A message from Adelaida · 100 visitors</p>
        <p className="mt-2 text-lg leading-snug" style={{ fontFamily: SERIF, color: CREAM }}>{t.text}</p>
      </div>
      <audio ref={ref} src={`${API}${t.audio_url}`} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} />
    </div>
  );
};

// Private stats page an influencer can bookmark: /with-stats/<token>.
export function InfluencerStats({ token: tokenProp, extra }) {
  const { token: tokenParam } = useParams();
  const token = tokenProp || tokenParam;
  const [s, setS] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { axios.get(`${API}/api/with-stats/${token}`).then((r) => setS(r.data)).catch(() => setErr(true)); }, [token]);
  return (
    <div className="min-h-screen px-6 py-16 sm:py-24" style={{ background: "#070D0B", color: CREAM }} data-testid="influencer-stats-page">
      <div className="max-w-2xl mx-auto">
        {extra && <a href="/soulumo/account" className="mb-8 inline-block text-xs uppercase tracking-[0.24em]" style={{ color: DIM, fontFamily: SANS }} data-testid="partner-area-back">← Account</a>}
        <p className="text-[0.7rem] tracking-[0.55em] font-medium" style={{ color: GOLD, fontFamily: SANS }}>{extra ? "SOULUMO PARTNER" : "RECLAIM"}</p>
        {err && <p className="mt-10 text-2xl" style={{ fontFamily: SERIF }} data-testid="influencer-stats-error">This link isn't valid.</p>}
        {s && (
          <>
            <h1 className="mt-6 text-4xl sm:text-5xl font-light" style={{ fontFamily: SERIF, color: CREAM }}>Thank you, {s.name}.</h1>
            <p className="mt-3 text-sm" style={{ color: DIM, fontFamily: SANS }}>Everyone who arrived through <span className="break-all" style={{ color: GOLD }}>{window.location.host}/with/{s.slug}</span></p>
            <PhotoUpload token={token} s={s} onDone={(photo_url) => setS({ ...s, photo_url })} />
            {s.thank_you ? <ThankYou t={s.thank_you} /> : (
              <p className="mt-8 text-sm" style={{ color: DIM, fontFamily: SANS }} data-testid="influencer-thanks-progress">
                At {s.thanks_at} visitors, Adelaida has a little message for you. {Math.max(0, s.thanks_at - s.visitors)} to go.
              </p>
            )}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Stat id="visitors" label={s.golf_partner || s.gym_partner ? "Clicks" : "Visitors"} value={s.visitors} />
              {s.golf_partner || s.gym_partner ? <Stat id="sales" label="Sales" value={s.sales} /> : <Stat id="lights" label="Lights lit" value={s.lights} />}
              <Stat id="signups" label="Sign-ups" value={s.signups} />
            </div>
            {s.demo && (
              <div className="mt-10" data-testid="influencer-demo-stats">
                <p className="text-[0.6rem] uppercase tracking-[0.28em]" style={{ color: GOLD, fontFamily: SANS }}>Your demo link</p>
                <div className="mt-3 grid grid-cols-3 gap-4">
                  <Stat id="demo-watched" label="Watched" value={s.demo.watched} />
                  <Stat id="demo-reached-join" label="Reached join" value={s.demo.reached_join} />
                  <Stat id="demo-joined" label="Joined" value={s.demo.joined} />
                </div>
                <p className="mt-3 text-xs" style={{ color: DIM, fontFamily: SANS }}>Share it: <span className="break-all" style={{ color: GOLD }}>{window.location.host}/api/soulumo/demo/share/{s.slug}</span></p>
              </div>
            )}
            {(s.golf_partner || s.gym_partner) && s.rank && (
              <p className="mt-8 text-2xl font-light" style={{ fontFamily: SERIF, color: CREAM }} data-testid="influencer-rank">
                You're <span style={{ color: GOLD }}>#{s.rank}</span> of {s.of} Founding Partners.
              </p>
            )}
            {s.kit && <div id="kit"><StarterKit kit={s.kit} gold={GOLD} cream={CREAM} dim={DIM} serif={SERIF} sans={SANS} /></div>}
            {extra}
            <p className="mt-10 text-lg italic" style={{ fontFamily: SERIF, color: DIM }}>{s.gym_partner ? "Every athlete you bring trains a little freer." : s.golf_partner ? "Every golfer you bring plays a little freer." : "Every light is one small act of good you helped begin."}</p>
          </>
        )}
      </div>
    </div>
  );
}
